import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { paperFinish, paperBorder, matchingPaperValue, rememberPaperPreference, rememberFrameColour } from '@theme/paper-finish-state';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {{ url: string, morphElementSelector?: string } | undefined} */
  #lastRequest;

  #paperPreference;

  #frameColourPreference = 'Black';

  get selectedFrameColour() { return this.#frameColourPreference; }

  #handleVariantChange = (event) => this.variantChanged(event);

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.#syncOptionAvailability();
    this.classList.add('cp-paper-controls-ready');
    this.addEventListener('change', this.#handleVariantChange);
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.removeEventListener('change', this.#handleVariantChange);
    this.#abortController?.abort();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    let target = event.target;
    if (target.matches('[data-paper-choice], [data-paper-border], [data-frame-colour]')) {
      const combined = this.querySelector('[data-option-name="paper-border"] select');
      if (!(combined instanceof HTMLSelectElement)) return;
      const paper = this.querySelector('[data-paper-choice]:checked')?.value;
      const border = this.querySelector('[data-paper-border]:checked')?.value || 'Borderless';
      const colour = this.querySelector('[data-frame-colour]:checked')?.value || this.#frameColourPreference;
      const value = matchingPaperValue(
        Array.from(combined.options).filter((option) => !option.disabled).map((option) => option.value), paper, border, colour
      );
      if (!value) return;
      combined.value = value;
      target = combined;
    }

    this.#syncOptionAvailability();

    const selectedOption =
      target instanceof HTMLSelectElement ? target.options[target.selectedIndex] : target;

    if (!selectedOption) return;

    const selectedVariant = this.#selectedAvailableVariant;
    if (selectedVariant) {
      selectedOption.dataset.variantId = String(selectedVariant.id);
    }

    this.updateSelectedOption(target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    let requestUrl = this.buildRequestUrl(selectedOption);
    // On the product page, ask only for the product section: every listener of the variant update
    // (price, gallery, summary, buy buttons, studio, badges) lives inside it, and the whole page
    // (about 400 KB of HTML to download and parse) made price changes take half a second.
    if (isOnProductPage && !loadsNewProduct && !isOnFeaturedProductSection && !requestUrl.includes('section_id=')) {
      const sectionId = this.closest('.shopify-section')?.id?.replace(/^shopify-section-/, '');
      if (sectionId) requestUrl += `${requestUrl.includes('?') ? '&' : '?'}section_id=${encodeURIComponent(sectionId)}`;
    }
    this.fetchUpdatedSection(requestUrl, morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Keeps each option dependent on the choices before it. Impossible values are
   * hidden instead of shown as unavailable, and a later selection is moved to
   * the first valid value when an earlier option changes.
   */
  #syncOptionAvailability() {
    const matrix = this.#variantOptionMatrix;
    if (!matrix) return;

    const controls = matrix.optionNames.map((_, index) =>
      this.querySelector(`[data-option-index="${index}"]`)
    );
    const selectedValues = controls.map((control) => this.#optionGroupValue(control));
    const paperIndex = matrix.optionNames.indexOf('Paper & border');
    if (paperIndex >= 0 && selectedValues[paperIndex]) {
      this.#paperPreference = rememberPaperPreference(selectedValues[paperIndex], this.#paperPreference);
      this.#frameColourPreference = rememberFrameColour(selectedValues[paperIndex], this.#frameColourPreference);
    }

    controls.forEach((control, index) => {
      if (!(control instanceof HTMLElement)) return;

      const availableValues = new Set(
        matrix.variants
          .filter((variant) => {
            if (!variant.available) return false;

            return selectedValues
              .slice(0, index)
              .every((selectedValue, previousIndex) => variant.options[previousIndex] === selectedValue);
          })
          .map((variant) => variant.options[index])
      );

      const optionInputs = /** @type {(HTMLOptionElement | HTMLInputElement)[]} */ (
        Array.from(control.querySelectorAll('option, input'))
      );
      optionInputs.forEach((optionInput) => {
        const isAvailable = availableValues.has(optionInput.value);

        if (optionInput instanceof HTMLOptionElement) {
          optionInput.hidden = !isAvailable;
          optionInput.disabled = !isAvailable;
        } else if (optionInput instanceof HTMLInputElement) {
          optionInput.disabled = !isAvailable;
          const label = optionInput.closest('label');
          if (label) label.hidden = !isAvailable;
        }
      });

      if (!availableValues.has(selectedValues[index] ?? '')) {
        const firstAvailableValue = matrix.optionNames[index] === 'Paper & border'
          ? matchingPaperValue([...availableValues], this.#paperPreference?.paper ?? paperFinish(selectedValues[index]), this.#paperPreference?.border ?? paperBorder(selectedValues[index]), this.#frameColourPreference)
          : optionInputs.find((optionInput) => availableValues.has(optionInput.value))?.value;
        if (firstAvailableValue) {
          this.#setOptionGroupValue(control, firstAvailableValue);
          selectedValues[index] = firstAvailableValue;
        }
      }

      const optionName = matrix.optionNames[index]?.toLowerCase();
      if (optionName === 'border') {
        const formatIndex = matrix.optionNames.findIndex((name) => name.toLowerCase() === 'format');
        const selectedFormat = formatIndex >= 0 ? selectedValues[formatIndex]?.toLowerCase() : '';
        control.hidden = selectedFormat !== 'unframed';
      }
    });
  }

  /**
   * @param {Element | null} control
   * @returns {string | undefined}
   */
  #optionGroupValue(control) {
    if (!control) return undefined;

    const select = control.querySelector('select');
    if (select instanceof HTMLSelectElement) return select.value;

    const checkedInput = control.querySelector('input:checked');
    return checkedInput instanceof HTMLInputElement ? checkedInput.value : undefined;
  }

  /**
   * @param {Element} control
   * @param {string} value
   */
  #setOptionGroupValue(control, value) {
    const select = control.querySelector('select');
    if (select instanceof HTMLSelectElement) {
      Array.from(select.options).forEach((option) => {
        const isSelected = option.value === value;
        option.selected = isSelected;
        option.toggleAttribute('selected', isSelected);
      });
      return;
    }

    Array.from(control.querySelectorAll('input')).forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const isSelected = input.value === value;
      input.checked = isSelected;
      input.dataset.currentChecked = String(isSelected);
    });
  }

  /**
   * @returns {{ optionNames: string[], variants: { id: number, available: boolean, options: string[] }[] } | null}
   */
  get #variantOptionMatrix() {
    const matrixElement = this.querySelector('[data-variant-option-matrix]');
    if (!(matrixElement instanceof HTMLScriptElement) || !matrixElement.textContent) return null;

    try {
      return JSON.parse(matrixElement.textContent);
    } catch (error) {
      console.error('Unable to parse variant option matrix', error);
      return null;
    }
  }

  /**
   * @returns {{ id: number, available: boolean, options: string[] } | undefined}
   */
  get #selectedAvailableVariant() {
    const matrix = this.#variantOptionMatrix;
    if (!matrix) return undefined;

    const selectedValues = matrix.optionNames.map((_, index) =>
      this.#optionGroupValue(this.querySelector(`[data-option-index="${index}"]`))
    );

    return matrix.variants.find(
      (variant) => variant.available && variant.options.every((value, index) => value === selectedValues[index])
    );
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  async fetchUpdatedSection(requestUrl, morphElementSelector) {
    this.#lastRequest = { url: requestUrl, morphElementSelector };
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 15000);

    try {
      const response = await fetch(requestUrl, { signal: controller.signal });
      if (!response.ok) throw new Error('Unable to update the selected variant');
      const responseText = await response.text();
      // Ignore an older response even if its transport did not honour cancellation.
      if (this.#abortController !== controller || controller.signal.aborted) return;
      this.#pendingRequestUrl = undefined;
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      html.querySelector('overflow-list[defer]')?.removeAttribute('defer');
      const textContent = html.querySelector('variant-picker script[type="application/json"]')?.textContent;
      if (!textContent || !this.selectedOptionId) throw new Error('The variant response was incomplete');
      const variant = JSON.parse(textContent);
      if (variant !== null && (!variant.id || typeof variant.available !== 'boolean')) {
        throw new Error('The variant response was invalid');
      }
      let newProduct;
      if (morphElementSelector === 'main') {
        this.updateMain(html);
      } else if (morphElementSelector) {
        this.updateElement(html, morphElementSelector);
      } else {
        const { overflowList } = this.refs;
        const wasSwatchesExpanded =
          overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';
        newProduct = this.updateVariantPicker(html);
        if (wasSwatchesExpanded) overflowList.showAll();
      }
      this.dispatchEvent(new VariantUpdateEvent(variant, this.selectedOptionId, {
        html, productId: this.dataset.productId ?? '', newProduct,
      }));
    } catch (error) {
      // Superseded requests and disconnected pickers do not invalidate the latest selection.
      if (this.#abortController !== controller || (!timedOut && error.name === 'AbortError')) return;
      this.#pendingRequestUrl = undefined;
      this.dispatchEvent(new CustomEvent('variant:error', {
        bubbles: true, detail: { productId: this.dataset.productId ?? '' },
      }));
      console.error('Variant update failed', error);
    } finally {
      clearTimeout(timeout);
    }
  }

  /** Retry rendering the current choice; never submit a purchase as a side effect. */
  retrySelection() {
    if (!this.#lastRequest) return;
    this.dispatchEvent(new VariantSelectedEvent({ id: this.selectedOptionId || '' }));
    return this.fetchUpdatedSection(this.#lastRequest.url, this.#lastRequest.morphElementSelector);
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
        this.#paperPreference = undefined;
        this.#frameColourPreference = 'Black';
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.#syncOptionAvailability();
    this.updateVariantPickerCss();
    this.classList.add('cp-paper-controls-ready');

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected][data-option-value-id], fieldset input:checked[data-option-value-id]');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected][data-option-value-id], fieldset input:checked[data-option-value-id]'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
